import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { useParams, useLocation } from "wouter";
import { ArrowLeft } from "@phosphor-icons/react";
import {
  useApi,
  useListIntegrations,
  useInvalidateLists,
} from "./components.js";
import { HelmLayout } from "./layouts.js";
import { getIntegrationProvider } from "./integration_registry.js";
import { CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { NiceNameEditor } from "./nice_name_editor.js";

const IntegrationPageContent = () => {
  const { orgSlug, integrationSlug } = useParams<{
    orgSlug: string;
    integrationSlug: string;
  }>();
  const { getToken } = useAuth();
  const pool = useApi();
  const invalidate = useInvalidateLists();
  const [, setLocation] = useLocation();
  const integrationsResult = useListIntegrations(`slug:${integrationSlug}`);
  const integration = integrationsResult.data?.entries[0] ?? null;
  const type = integration?.type ?? null;
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (integrationsResult.isLoading) return;
    if (integrationsResult.error) {
      setError(integrationsResult.error);
      setLoaded(true);
      return;
    }
    if (!integration) {
      setError(`Integration not found: ${integrationSlug}`);
      setLoaded(true);
      return;
    }
    if (!integration.configBlobSlug) {
      setLoaded(true);
      return;
    }
    const api = pool.acquire();
    let cancelled = false;
    const load = async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) return;
        const configBase64 = await orgApi.getBlob(integration.configBlobSlug!);
        if (configBase64) {
          const saved = JSON.parse(atob(configBase64));
          if (!cancelled && saved && typeof saved === "object") {
            setConfig(saved as Record<string, unknown>);
          }
        }
      } catch (err) {
        if (!cancelled) setError(`${err}`);
      } finally {
        pool.release();
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    getToken,
    pool,
    integrationSlug,
    integration,
    integrationsResult.isLoading,
    integrationsResult.error,
  ]);

  const provider = type ? getIntegrationProvider(type) : undefined;
  const Icon = provider?.icon;

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    const api = pool.acquire();
    try {
      const token = await getToken();
      if (!token) return;
      const authedApi = await api.authenticate(token);
      const orgApi = authedApi.org;
      if (!orgApi) return;
      await orgApi.connectIntegration(integrationSlug, config);
      invalidate("listIntegrations");
    } catch (err) {
      setError(`${err}`);
    } finally {
      pool.release();
      setSaving(false);
    }
  }, [getToken, pool, integrationSlug, config, invalidate]);

  const handleNiceNameChange = useCallback(
    async (niceName: string | null) => {
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) return;
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) return;
        await orgApi.executeAction({
          verb: "updateIntegrationNiceName",
          params: { integrationSlug, niceName },
        });
        invalidate("listIntegrations");
      } finally {
        pool.release();
      }
    },
    [getToken, pool, integrationSlug, invalidate],
  );

  const handleBack = useCallback(() => {
    if (provider?.kind === "channel") {
      setLocation(`/o/${orgSlug}/channels`);
    } else {
      setLocation(`/o/${orgSlug}/sources`);
    }
  }, [provider, orgSlug, setLocation]);

  if (!loaded) {
    return <div className="text-muted-foreground">Loading...</div>;
  }
  if (error && !provider) {
    return <div className="text-destructive">{error}</div>;
  }
  if (!provider) {
    return <div className="text-destructive">Unknown integration type</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-row items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
          {Icon ? <Icon /> : null}
        </div>
        <div className="flex-1">
          <NiceNameEditor
            niceName={integration?.niceName ?? null}
            slug={integrationSlug!}
            onSave={handleNiceNameChange}
          />
          <CardDescription>
            {provider.label} ·{" "}
            {provider.kind === "channel" ? "Output channel" : "Input source"}
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="size-4" />
          Back
        </Button>
      </div>
      <provider.ConfigEditor config={config} update={setConfig} />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end">
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

export const IntegrationPage = () => (
  <HelmLayout>
    <IntegrationPageContent />
  </HelmLayout>
);
